import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { ShieldCheck, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LEGAL_ENTITY } from "../lib/entity";

/**
 * ComplianceIDCard
 *
 * Single-screen trust artifact for banks, attorneys, KYC reviewers and future
 * compliance auditors. Surfaces the legal entity, formation facts, EIN,
 * principal office, and live operational status of the compliance-adjacent
 * integrations (SumSub KYC, Monaxa partner API, transactional email, Stripe).
 *
 * Mounts at the top of the Admin /settings page. The parent passes its
 * already-loaded `settings` object down (masked secrets only) so the card
 * adds no duplicate GET /settings; the SumSub health probe is the one
 * request the parent doesn't already make on load. Admin-only — relies on
 * the caller already being authenticated (route guard handles redirect).
 */

const tone = {
  ok:   { badge: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", dot: "bg-emerald-400" },
  warn: { badge: "bg-amber-500/15 border-amber-500/40 text-amber-300",       dot: "bg-amber-400" },
  off:  { badge: "bg-slate-500/15 border-slate-500/40 text-slate-300",       dot: "bg-slate-400" },
};

const StatusRow = ({ label, status, detail }) => {
  const t = tone[status] || tone.off;
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0" data-testid={`compliance-id-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        <span className="text-gray-300">{label}</span>
      </div>
      <Badge className={`text-[10px] uppercase tracking-wider border ${t.badge}`}>
        {detail}
      </Badge>
    </div>
  );
};

const Field = ({ label, value, copy }) => {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p className="text-sm text-gray-200 break-words">{value}</p>
      </div>
      {copy && (
        <button
          type="button"
          onClick={onCopy}
          className="text-gray-500 hover:text-gold transition-colors mt-3"
          aria-label={`Copy ${label}`}
          data-testid={`compliance-id-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Copy size={13} />
        </button>
      )}
    </div>
  );
};

const ComplianceIDCard = ({ settings = {}, settingsLoading = false }) => {
  const [kyc, setKyc] = useState({ status: "off", detail: "loading" });
  const [stripe, setStripe] = useState({ status: "off", detail: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/onboarding/sumsub/health");
        if (cancelled) return;
        if (!r.data?.configured) setKyc({ status: "warn", detail: "not configured" });
        else if (r.data?.auth_ok && r.data?.enabled) setKyc({ status: "ok", detail: "live" });
        else if (r.data?.auth_ok) setKyc({ status: "warn", detail: "configured · disabled" });
        else setKyc({ status: "warn", detail: "auth failed" });
      } catch {
        if (!cancelled) setKyc({ status: "off", detail: "unreachable" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stripe row probes the backend's EFFECTIVE key resolution (DB-then-env)
  // rather than the masked settings payload, which can't see the env
  // fallback (forge-20260611-001). Presence only — no key material.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/payments/stripe/health");
        if (cancelled) return;
        if (!r.data?.configured) {
          setStripe({ status: "off", detail: "not configured" });
        } else if (r.data.enabled === false) {
          // Configured but force-disabled via the integration gate — every
          // outbound Stripe route short-circuits, so don't overstate readiness.
          setStripe({ status: "warn", detail: "configured · disabled" });
        } else {
          const bits = [`via ${r.data.source}`];
          if (r.data.mode && r.data.mode !== "live") bits.push(`${r.data.mode} mode`);
          setStripe({ status: "ok", detail: bits.join(" · ") });
        }
      } catch {
        if (!cancelled) setStripe({ status: "off", detail: "unreachable" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Derived from the parent's settings (masked secrets — presence only).
  const resend = settingsLoading
    ? { status: "off", detail: "loading" }
    : settings.resend_api_key
      ? { status: "ok", detail: "resend" }
      : settings.smtp_host
        ? { status: "warn", detail: "smtp fallback" }
        : { status: "off", detail: "not configured" };
  // The backend's MonaxaHttpClient.from_settings() requires BOTH the key and
  // the URL — mirror that so the card never overstates readiness.
  const monaxa = settingsLoading
    ? { status: "off", detail: "loading" }
    : settings.monaxa_api_key && settings.monaxa_api_url
      ? { status: "ok", detail: "configured" }
      : { status: "off", detail: "not configured" };
  const tls = window.location.protocol === "https:"
    ? { status: "ok", detail: "enforced" }
    : { status: "warn", detail: "http (local)" };

  return (
    <Card className="bg-surface border-gold/20 lg:col-span-2" data-testid="compliance-id-card">
      <CardHeader>
        <CardTitle className="text-white font-serif flex items-center gap-2">
          <ShieldCheck className="text-gold" size={20} />
          Compliance ID Card
          <Badge className="ml-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-wider">
            <CheckCircle2 size={10} className="mr-1" />
            Active
          </Badge>
        </CardTitle>
        <CardDescription>
          Single-screen artifact for banks, attorneys, KYC reviewers and auditors.
          Screenshot or share read-only to prove who runs echosensecapital.com.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
          {/* Left column: legal entity facts */}
          <div>
            <Field label="Legal Name"        value={LEGAL_ENTITY.name}    copy />
            <Field label="Jurisdiction"      value={LEGAL_ENTITY.jurisdiction} />
            <Field label="State File No."    value={LEGAL_ENTITY.fileNo}  copy />
            <Field label="EIN (US Tax ID)"   value={LEGAL_ENTITY.ein}     copy />
            <Field label="Principal Office"  value={LEGAL_ENTITY.address} copy />
            <Field label="Registered Agent"  value={LEGAL_ENTITY.registeredAgent} />
          </div>

          {/* Right column: live integration status */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 mt-1">
              Live Integration Status
            </p>
            <StatusRow label="KYC / AML (SumSub)" status={kyc.status}    detail={kyc.detail} />
            <StatusRow label="Partner API (Monaxa)" status={monaxa.status} detail={monaxa.detail} />
            <StatusRow label="Transactional Email" status={resend.status}  detail={resend.detail} />
            <StatusRow label="Payments (Stripe)"   status={stripe.status}  detail={stripe.detail} />
            <StatusRow label="TLS / HTTPS"         status={tls.status}     detail={tls.detail} />
            <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
              Status checked on this page load. Refresh to re-test. SumSub probe is signed and side-effect-free.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplianceIDCard;
